import { useEffect, useState } from "react";
import api, { formatErr, fmtUsd } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const LEVERAGES = [1, 2, 5, 10, 20, 50, 100];

export default function TradePanel({ symbol, markPrice, onTraded }) {
  const [tab, setTab] = useState("MARKET"); // MARKET | LIMIT
  const [side, setSide] = useState("BUY");
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [leverage, setLeverage] = useState(1);
  const [sl, setSL] = useState("");
  const [tp, setTP] = useState("");
  const [busy, setBusy] = useState(false);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    api.get("/wallet").then((r) => setBalance(r.data.balance)).catch(() => {});
  }, [onTraded]);

  useEffect(() => {
    if (tab === "LIMIT" && markPrice && !price) setPrice(String(markPrice));
  }, [tab, markPrice, price]);

  const px = tab === "MARKET" ? markPrice : parseFloat(price) || 0;
  const q = parseFloat(qty) || 0;
  const notional = px * q;
  const margin = leverage ? notional / leverage : 0;

  const setPercent = (pct) => {
    if (!px) return;
    const useMargin = balance * pct;
    const useNotional = useMargin * leverage;
    const useQty = useNotional / px;
    setQty(useQty.toFixed(6));
  };

  const submit = async () => {
    if (!q) return toast.error("Enter quantity");
    if (margin > balance) return toast.error("Insufficient margin");
    setBusy(true);
    try {
      const payload = {
        symbol, side, type: tab, quantity: q, leverage,
        price: tab === "LIMIT" ? parseFloat(price) : null,
        stop_loss: sl ? parseFloat(sl) : null,
        take_profit: tp ? parseFloat(tp) : null,
      };
      const { data } = await api.post("/trade/order", payload);
      toast.success(`${tab} ${side} ${q} ${symbol.replace("USDT", "")} · ${data.status}`);
      setQty(""); setSL(""); setTP("");
      onTraded?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const sideColor = side === "BUY" ? "#0ECB81" : "#F6465D";

  return (
    <div className="bg-[#111827] border-t border-[#1F2937] flex flex-col" data-testid="trade-panel">
      <div className="flex border-b border-[#1F2937]">
        {["MARKET", "LIMIT"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-xs overline transition-colors ${tab === t ? "text-[#FCD535] border-b-2 border-[#FCD535]" : "text-[#9CA3AF] hover:text-white"}`}
            data-testid={`tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>

      <div className="p-3 space-y-3">
        {/* Side toggle */}
        <div className="grid grid-cols-2 gap-1">
          <Button onClick={() => setSide("BUY")} data-testid="side-buy"
            className={`h-9 rounded-sm font-semibold ${side === "BUY" ? "bg-[#0ECB81] hover:bg-[#0ECB81]/90 text-white" : "bg-[#1F2937] hover:bg-[#1F2937]/70 text-[#9CA3AF]"}`}>
            BUY / LONG
          </Button>
          <Button onClick={() => setSide("SELL")} data-testid="side-sell"
            className={`h-9 rounded-sm font-semibold ${side === "SELL" ? "bg-[#F6465D] hover:bg-[#F6465D]/90 text-white" : "bg-[#1F2937] hover:bg-[#1F2937]/70 text-[#9CA3AF]"}`}>
            SELL / SHORT
          </Button>
        </div>

        {/* Leverage */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <Label className="overline">Leverage</Label>
            <span className="mono text-xs text-[#FCD535] font-semibold" data-testid="leverage-value">{leverage}x</span>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {LEVERAGES.map((l) => (
              <button key={l} onClick={() => setLeverage(l)}
                className={`py-1 text-[11px] mono rounded-sm transition-colors ${leverage === l ? "bg-[#FCD535] text-black font-semibold" : "bg-[#0A0E17] text-[#9CA3AF] hover:bg-[#1F2937]"}`}
                data-testid={`leverage-${l}`}>{l}x</button>
            ))}
          </div>
        </div>

        {/* Price (limit only) */}
        {tab === "LIMIT" && (
          <div>
            <Label className="overline">Price (USDT)</Label>
            <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} step="any"
              className="h-9 mono bg-[#0A0E17] border-[#1F2937] rounded-sm mt-1" data-testid="price-input" />
          </div>
        )}

        {/* Quantity */}
        <div>
          <Label className="overline">Quantity ({symbol?.replace("USDT", "")})</Label>
          <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} step="any" placeholder="0.00"
            className="h-9 mono bg-[#0A0E17] border-[#1F2937] rounded-sm mt-1" data-testid="qty-input" />
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[0.25, 0.5, 0.75, 1].map((p) => (
              <button key={p} onClick={() => setPercent(p)}
                className="py-1 text-[10px] bg-[#0A0E17] hover:bg-[#1F2937] rounded-sm text-[#9CA3AF]"
                data-testid={`pct-${p * 100}`}>
                {p * 100}%
              </button>
            ))}
          </div>
        </div>

        {/* SL/TP */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="overline text-[#F6465D]">Stop Loss</Label>
            <Input type="number" value={sl} onChange={(e) => setSL(e.target.value)} step="any" placeholder="—"
              className="h-8 mono text-xs bg-[#0A0E17] border-[#1F2937] rounded-sm mt-1" data-testid="sl-input" />
          </div>
          <div>
            <Label className="overline text-[#0ECB81]">Take Profit</Label>
            <Input type="number" value={tp} onChange={(e) => setTP(e.target.value)} step="any" placeholder="—"
              className="h-8 mono text-xs bg-[#0A0E17] border-[#1F2937] rounded-sm mt-1" data-testid="tp-input" />
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-1 text-xs pt-2 border-t border-[#1F2937]">
          <div className="flex justify-between"><span className="text-[#9CA3AF]">Notional</span><span className="mono">${fmtUsd(notional)}</span></div>
          <div className="flex justify-between"><span className="text-[#9CA3AF]">Margin</span><span className="mono">${fmtUsd(margin)}</span></div>
          <div className="flex justify-between"><span className="text-[#9CA3AF]">Available</span><span className="mono">${fmtUsd(balance)}</span></div>
        </div>

        <Button onClick={submit} disabled={busy || !q}
          className="w-full h-10 rounded-sm font-semibold text-white"
          style={{ background: sideColor }}
          data-testid="submit-order-button">
          {busy ? "Placing..." : `${side === "BUY" ? "Buy/Long" : "Sell/Short"} ${symbol?.replace("USDT", "")}`}
        </Button>
      </div>
    </div>
  );
}
